import { useEffect, useMemo, useState } from "react";
import { Loader2, Sprout, Move, Scale } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { getTimeline } from "@/lib/api";
import { useGardens } from "@/contexts/GardenContext";

const eventColor = {
    sow_planned: "bg-[var(--vm-sage)]/40 text-[var(--vm-text)] border-[var(--vm-sage)]",
    sow_actual: "bg-[var(--vm-accent)]/20 text-[var(--vm-text)] border-[var(--vm-accent)]",
    transplant_planned: "bg-[var(--vm-tertiary)]/30 text-[var(--vm-text)] border-[var(--vm-tertiary)]",
    transplant_actual: "bg-[var(--vm-tertiary)]/40 text-[var(--vm-text)] border-[var(--vm-tertiary)]",
    harvest_start: "bg-[var(--vm-secondary)]/25 text-[var(--vm-text)] border-[var(--vm-secondary)]",
    harvest_end: "bg-[var(--vm-secondary)]/15 text-[var(--vm-text-secondary)] border-[var(--vm-secondary)]/50",
};

const eventIcon = (type) => {
    if (type.startsWith("sow")) return Sprout;
    if (type.startsWith("transplant")) return Move;
    return Scale;
};

const monthLabel = (m) => new Date(2026, m, 1).toLocaleDateString("en-IE", { month: "long" });

export default function Timeline() {
    const { activeGardenId } = useGardens();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getTimeline(activeGardenId || undefined)
            .then((r) => setEvents(r.events || []))
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, [activeGardenId]);

    // Group by year-month
    const grouped = useMemo(() => {
        const map = {};
        for (const e of events) {
            try {
                const d = new Date(e.date);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                if (!map[key]) map[key] = { year: d.getFullYear(), month: d.getMonth(), items: [] };
                map[key].items.push(e);
            } catch {}
        }
        return Object.values(map).sort((a, b) => a.year - b.year || a.month - b.month);
    }, [events]);

    return (
        <div className="space-y-10" data-testid="timeline-page">
            <PageHeader
                overline="Calendar of Tasks"
                title="Garden Timeline"
                description="Sow → transplant → harvest, all in one place. Hover any block for details."
            />

            {loading ? (
                <div className="text-[var(--vm-text-muted)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading the calendar…</div>
            ) : events.length === 0 ? (
                <div className="journal-card p-12 text-center">
                    <h3 className="font-serif-display text-2xl mb-2">No timeline yet</h3>
                    <p className="text-[var(--vm-text-secondary)]">Add crops with sowing/harvest dates to populate this view.</p>
                </div>
            ) : (
                <div className="space-y-10">
                    {grouped.map(({ year, month, items }) => (
                        <section key={`${year}-${month}`} data-testid={`month-${year}-${month}`}>
                            <div className="flex items-baseline gap-3 mb-4">
                                <h2 className="font-serif-display text-3xl text-[var(--vm-text)]">{monthLabel(month)}</h2>
                                <div className="text-sm text-[var(--vm-text-muted)]">{year} · {items.length} events</div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                {items.map((e, i) => {
                                    const Icon = eventIcon(e.type);
                                    return (
                                        <div
                                            key={`${e.crop_id}-${e.type}-${i}`}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${eventColor[e.type] || ""}`}
                                            data-testid={`event-${e.type}-${e.crop_id}`}
                                        >
                                            <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{e.crop_name} {e.variety && <span className="italic text-[var(--vm-text-muted)]">'{e.variety}'</span>}</div>
                                                <div className="text-xs text-[var(--vm-text-muted)] truncate">{e.bed_name} · {e.type.replace(/_/g, " ")}</div>
                                            </div>
                                            <div className="text-xs text-[var(--vm-text-muted)] shrink-0">{e.date.slice(5)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {/* Legend */}
            <div className="journal-card p-5 text-xs" data-testid="timeline-legend">
                <div className="overline text-[var(--vm-sage)] mb-2">Legend</div>
                <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--vm-sage)]/60 border border-[var(--vm-sage)]"></span>Sow planned</span>
                    <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--vm-tertiary)]/60 border border-[var(--vm-tertiary)]"></span>Transplant</span>
                    <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[var(--vm-secondary)]/40 border border-[var(--vm-secondary)]"></span>Harvest</span>
                </div>
            </div>
        </div>
    );
}
